// ════════════════════════════════════════════════════════════════════════════
// MediaLibraryPicker — modal that lets admin reuse previously uploaded files
// instead of re-uploading. Used from the homepage banner editor and anywhere
// else an image URL is needed.
//
// Power: paginated grid + search + "Upload New" (signed Cloudinary upload that
// also registers a FileObject row so the file shows up in the library next time).
// ════════════════════════════════════════════════════════════════════════════
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Search, Upload, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { UploadedFile } from '@terra/api-client';
import { Spinner } from '@terra/ui';

import { api } from '../lib/api';

interface MediaLibraryPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (file: UploadedFile) => void;
  /** Hint for the upload subfolder so library items group sensibly. */
  uploadFolder?: string;
  /** Title shown in the header. */
  title?: string;
}

export function MediaLibraryPicker({
  open,
  onClose,
  onSelect,
  uploadFolder = 'banners',
  title = 'Select an image',
}: MediaLibraryPickerProps) {
  const qc = useQueryClient();
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Debounce search so we don't refetch on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchDraft.trim());
      setPage(1);
    }, 250);
    return () => clearTimeout(id);
  }, [searchDraft]);

  // Close on ESC for accessibility.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const filesQuery = useQuery({
    queryKey: ['media-library', search, page],
    queryFn: () => api.files.list({ ...(search ? { q: search } : {}), sort: 'newest', page }),
    enabled: open,
  });

  // Upload via signed Cloudinary direct-upload, then register so the file
  // joins the library list on next refetch.
  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<UploadedFile> => {
      const sig = await api.files.getUploadSignature({ folder: uploadFolder });
      const fd = new FormData();
      fd.append('file', file);
      fd.append('api_key', sig.apiKey);
      fd.append('timestamp', String(sig.timestamp));
      fd.append('signature', sig.signature);
      fd.append('folder', sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloudName}/image/upload`, {
        method: 'POST',
        body: fd,
      });
      const out = (await res.json()) as { secure_url?: string };
      if (!out.secure_url) throw new Error('Cloudinary upload returned no secure_url');
      return api.files.register({
        name: file.name,
        url: out.secure_url,
        contentType: file.type || 'image/*',
        sizeBytes: file.size,
        visibility: 'PUBLIC',
      });
    },
    onSuccess: (registered) => {
      void qc.invalidateQueries({ queryKey: ['media-library'] });
      onSelect(registered);
      onClose();
    },
  });

  if (!open) return null;

  const files = filesQuery.data?.data ?? [];
  const pagination = filesQuery.data?.page;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className="bg-surface flex h-[80vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-border/60 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-2">
            <ImageIcon className="text-ink-muted h-5 w-5" />
            <h2 className="text-ink text-[15px] font-bold">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-muted hover:bg-surface-subtle grid h-8 w-8 place-items-center rounded-lg transition-colors"
            aria-label="Close media library"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="border-border/60 flex flex-wrap items-center gap-3 border-b px-5 py-3">
          <div className="bg-surface-subtle/50 border-border/40 focus-within:ring-brand-primary/50 flex h-9 w-full max-w-sm items-center rounded-xl border px-3 transition-shadow focus-within:ring-1">
            <Search className="text-ink-muted mr-2 h-4 w-4" />
            <input
              placeholder="Search by filename…"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              className="text-ink placeholder:text-ink-muted/60 w-full bg-transparent text-[13px] font-medium outline-none"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setUploading(true);
                  uploadMutation.mutate(f, {
                    onSettled: () => {
                      setUploading(false);
                      if (uploadInputRef.current) uploadInputRef.current.value = '';
                    },
                  });
                }
              }}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => uploadInputRef.current?.click()}
              className="bg-brand-primary hover:bg-brand-primary/90 flex h-9 items-center gap-2 rounded-xl px-4 text-[13px] font-bold text-white transition-colors disabled:opacity-50"
            >
              {uploading ? (
                <Spinner size="sm" className="text-white" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              Upload New
            </button>
          </div>
        </div>

        {/* Body — grid */}
        <div className="flex-1 overflow-y-auto p-5">
          {filesQuery.isLoading ? (
            <div className="grid h-full place-items-center">
              <Spinner size="lg" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-ink-muted grid h-full place-items-center text-[13px] font-medium">
              {search ? (
                <p>No files match &quot;{search}&quot;.</p>
              ) : (
                <p>No uploaded files yet. Click &quot;Upload New&quot; to add the first one.</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {files.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  onClick={() => {
                    onSelect(file);
                    onClose();
                  }}
                  className="border-border/60 bg-surface hover:border-brand-primary group flex flex-col overflow-hidden rounded-xl border text-left transition-all hover:shadow-md"
                  title={file.name}
                >
                  <div className="bg-surface-subtle/40 relative aspect-square overflow-hidden">
                    {file.url ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                      />
                    ) : (
                      <div className="text-ink-muted grid h-full place-items-center">
                        <ImageIcon className="h-8 w-8 opacity-40" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-0.5 px-2.5 py-2">
                    <p className="text-ink truncate text-[12px] font-bold">{file.name}</p>
                    <p className="text-ink-muted text-[10px] font-medium">
                      {(file.sizeBytes / 1024).toFixed(0)} KB
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer — pagination */}
        {pagination && pagination.pageCount > 1 && (
          <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-t px-5 py-3">
            <p className="text-ink-muted text-[12px] font-medium">
              Page {pagination.page} of {pagination.pageCount}
              <span className="text-ink-muted/70 ml-2">({pagination.total} total)</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="bg-surface border-border/60 text-ink hover:bg-surface-subtle h-8 rounded-lg border px-3 text-[12px] font-bold transition-colors disabled:opacity-50"
              >
                Prev
              </button>
              <button
                type="button"
                disabled={pagination.page >= pagination.pageCount}
                onClick={() => setPage((p) => p + 1)}
                className="bg-surface border-border/60 text-ink hover:bg-surface-subtle h-8 rounded-lg border px-3 text-[12px] font-bold transition-colors disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
